import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X, Camera, Pause, Play } from 'lucide-react';
import { galleryImages } from '../data/mock';
import { AnimateOnScroll } from '../hooks/useScrollAnimation';
import BasqueFlag from './BasqueFlag';

const Gallery = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);

  // Auto-play carousel
  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % galleryImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isPlaying]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % galleryImages.length);
  }, []);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  return (
    <section id="galerie" className="section-padding bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-1/4 left-0 w-64 h-64 bg-[#9B1B30]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#006633]/10 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative">
        {/* Header */}
        <AnimateOnScroll animation="fadeUp">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 bg-[#9B1B30] text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
              <Camera size={16} />
              <span>Visite virtuelle</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-6">
              Découvrez notre <span className="text-[#9B1B30]">laverie</span>
            </h2>
            <p className="text-lg text-gray-400">
              Un espace moderne, propre et accueillant au cœur de Bayonne.
            </p>
          </div>
        </AnimateOnScroll>

        {/* Main Carousel */}
        <AnimateOnScroll animation="scale">
          <div className="relative">
            {/* Main Image Container */}
            <div 
              className="relative rounded-3xl overflow-hidden shadow-2xl cursor-pointer group"
              style={{ aspectRatio: '16/9' }}
              onClick={() => setSelectedImage(currentIndex)}
            >
              {/* Images with crossfade transition */}
              {galleryImages.map((image, index) => (
                <img
                  key={image.id}
                  src={image.url}
                  alt={image.alt}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                    index === currentIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}
              
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
              
              {/* Info overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className="flex items-end justify-between">
                  <div>
                    <BasqueFlag size={36} className="mb-3" />
                    <h3 className="text-xl md:text-2xl font-black text-white mb-1">
                      {galleryImages[currentIndex].alt}
                    </h3>
                    <p className="text-white/70 text-sm">Lavoir des Remparts • Bayonne</p>
                  </div>
                  <div className="text-white/80 text-sm font-medium bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-full">
                    {currentIndex + 1} / {galleryImages.length}
                  </div>
                </div>
              </div>

              {/* Click to expand hint */}
              <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                Cliquez pour agrandir
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-900 p-3 rounded-full shadow-lg transition-all hover:scale-110 z-10"
              aria-label="Image précédente"
            >
              <ChevronLeft size={24} />
            </button>
            
            <button
              onClick={(e) => { e.stopPropagation(); goToNext(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-900 p-3 rounded-full shadow-lg transition-all hover:scale-110 z-10"
              aria-label="Image suivante"
            >
              <ChevronRight size={24} />
            </button>

            {/* Play/Pause button */}
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="absolute top-4 left-4 bg-white/90 hover:bg-white text-gray-900 p-2 rounded-full shadow-lg transition-all z-10"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause size={18} /> : <Play size={18} />}
            </button>
          </div>
        </AnimateOnScroll>

        {/* Thumbnail Navigation */}
        <div className="mt-6 flex justify-center gap-3 flex-wrap">
          {galleryImages.map((image, index) => (
            <button
              key={image.id}
              onClick={() => goToSlide(index)}
              className={`relative rounded-xl overflow-hidden transition-all duration-300 ${
                index === currentIndex 
                  ? 'ring-2 ring-[#9B1B30] ring-offset-2 ring-offset-gray-900 scale-105' 
                  : 'opacity-60 hover:opacity-100'
              }`}
              style={{ width: '80px', height: '60px' }}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>

        {/* Dots indicator (mobile) */}
        <div className="mt-6 flex justify-center gap-2 md:hidden">
          {galleryImages.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                index === currentIndex 
                  ? 'bg-[#9B1B30] w-8' 
                  : 'bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Aller à l'image ${index + 1}`}
            />
          ))}
        </div>

        {/* Quality badges */}
        <AnimateOnScroll animation="fadeUp">
          <div className="mt-12 flex flex-wrap justify-center gap-3">
            {[
              'Machines Speed Queen',
              'Sèche-linge grande capacité',
              'Accès PMR',
              'Café & Confiseries',
              'Sièges confortables',
              'Point Pickup'
            ].map((badge, i) => (
              <span 
                key={i}
                className="bg-white/10 backdrop-blur-sm px-5 py-2.5 rounded-full text-white font-medium border border-white/20"
              >
                ✓ {badge}
              </span>
            ))}
          </div>
        </AnimateOnScroll>
      </div>

      {/* Fullscreen Lightbox */}
      {selectedImage !== null && (
        <div 
          className="fixed inset-0 z-50 bg-black/98 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-6 right-6 text-white hover:text-[#9B1B30] transition-colors z-10 bg-white/10 p-2 rounded-full"
          >
            <X size={28} />
          </button>
          
          <button
            onClick={(e) => { e.stopPropagation(); setSelectedImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length); }}
            className="absolute left-4 md:left-8 bg-white/10 backdrop-blur-sm text-white p-4 rounded-full hover:bg-[#9B1B30] transition-colors"
          >
            <ChevronLeft size={28} />
          </button>
          
          <img
            src={galleryImages[selectedImage].url}
            alt={galleryImages[selectedImage].alt}
            className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          <button
            onClick={(e) => { e.stopPropagation(); setSelectedImage((prev) => (prev + 1) % galleryImages.length); }}
            className="absolute right-4 md:right-8 bg-white/10 backdrop-blur-sm text-white p-4 rounded-full hover:bg-[#9B1B30] transition-colors"
          >
            <ChevronRight size={28} />
          </button>

          <div className="absolute bottom-6 text-center text-white">
            <p className="font-bold text-xl mb-1">{galleryImages[selectedImage].alt}</p>
            <p className="text-gray-400">{selectedImage + 1} / {galleryImages.length}</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
